import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDateBR } from '../utils/dateUtils';
import { fixEncodingIssues } from '../utils/textUtils';

export interface MedicaoExportData {
  id?: number | string;
  type: 'cliente' | 'agregado';
  clienteNome?: string;
  agregadoNome?: string;
  periodoInicial?: string;
  periodoFinal?: string;
  valorTotal?: number;
  statusNome?: string;
  dataCriacao?: string;
  observacoes?: string;
}

export interface OsItemExportData {
  id: number;
  data_atendimento?: string;
  cliente_nome?: string;
  agregado_nome?: string;
  motorista_nome?: string;
  placa?: string;
  tipo_veiculo?: string;
  descricao?: string;
  valor_unit_servi?: number;
  retirada?: string;
  entrega?: string;
  total_os?: number;
  valor_total_repasses?: number;
  status_nome?: string;
  // Campos de Horário e Ajudantes
  horario_inicio?: string;
  hora_inicio?: string;
  horario_final?: string;
  hora_fim?: string;
  hora_termino?: string;
  hora_total?: string;
  total_hora?: string;
  quantidade_ajudante?: number;
  qtd_ajudante?: number;
  valor_ajudante?: number;
  ajudante_repasse?: number;
  subtotal_viagem?: number;
  valor_unit_ajud?: number;
  // Relações brutas opcionais
  fk_item_servico?: any;
  item_servico_rel?: any;
  tipo_servico?: any;
  veiculos?: any;
  veiculo_modelo?: any;
}

// Helpers de formatação e sanitização para compatibilidade com Microsoft Excel e XML
export function escapeXml(unsafe?: any): string {
  if (unsafe === null || unsafe === undefined) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function cleanText(val?: any): string {
  if (val === null || val === undefined) return '';
  return escapeXml(fixEncodingIssues(String(val)).trim());
}

/**
 * Resolve o Tipo de Veículo textual (HR, TRUCK, TOCO, MUNCK, PICAPE, CARRETA, VUC, etc.)
 * a partir das relações de item_servico, detalhes_do_pedido, modelo do veículo ou descrição.
 */
export function resolveTipoVeiculoDisplay(os: any): string {
  if (!os) return '-';

  // 1. Relação direta de item_servico -> tipo_servico -> tipo (ex: HR, TRUCK, TOCO)
  const ts = os.fk_item_servico?.tipo_servico?.tipo || 
             os.item_servico_rel?.tipo_servico?.tipo || 
             os.tipo_servico?.tipo;
  if (ts && typeof ts === 'string' && ts.toLowerCase() !== 'ajudante') {
    return ts.toUpperCase().trim();
  }

  // 2. Do texto da descrição do item_servico (ex: "CAMINHÃO TRUCK ATÉ 11 T", "HR PARA ATÉ 1.500 KG")
  const servDesc = os.fk_item_servico?.servicos || 
                   os.item_servico_rel?.servicos || 
                   os.item_servico_nome || 
                   os.servicos || 
                   '';
  if (servDesc && typeof servDesc === 'string') {
    const matchServ = servDesc.match(/\b(TRUCK|TOCO|HR|VUC|MUNCK|PICAPE|CARRETA|3\/4|FURG[AÃ]O|VAN|BONGO)\b/i);
    if (matchServ) return matchServ[0].toUpperCase().trim();
  }

  // 3. Se for string explícita em tipo_veiculo
  if (typeof os.tipo_veiculo === 'string' && os.tipo_veiculo && os.tipo_veiculo !== '-' && os.tipo_veiculo !== '[object Object]') {
    const directMatch = os.tipo_veiculo.match(/\b(TRUCK|TOCO|HR|VUC|MUNCK|PICAPE|CARRETA|3\/4|FURG[AÃ]O|VAN|BONGO)\b/i);
    if (directMatch) return directMatch[0].toUpperCase().trim();
    if (os.tipo_veiculo.trim()) return os.tipo_veiculo.trim();
  }

  // 4. Do modelo do veículo cadastrado
  const modelo = os.veiculos?.veiculo_modelo?.modelo || 
                 os.veiculo_modelo?.modelo || 
                 os.modelo_nome || 
                 '';
  if (modelo && typeof modelo === 'string') {
    if (/HR/i.test(modelo)) return 'HR';
    if (/BONGO/i.test(modelo)) return 'HR / BONGO';
    if (/1113|1313|1418|1620|CARGO/i.test(modelo)) return 'TRUCK';
    if (/MONTANA|SAVEIRO|STRADA/i.test(modelo)) return 'PICAPE';
    return modelo.toUpperCase().trim();
  }

  // 5. De objeto tipo_veiculo
  if (os.tipo_veiculo && typeof os.tipo_veiculo === 'object' && os.tipo_veiculo.tipo_veiculo) {
    const tvText = String(os.tipo_veiculo.tipo_veiculo);
    const m = tvText.match(/\b(TRUCK|TOCO|HR|VUC|MUNCK|PICAPE|CARRETA|3\/4|FURG[AÃ]O|VAN|BONGO)\b/i);
    if (m) return m[0].toUpperCase().trim();
    return tvText.trim();
  }

  // 6. Da descrição da O.S.
  const matchDesc = String(os.descricao || '').match(/\b(TRUCK|TOCO|HR|VUC|MUNCK|PICAPE|CARRETA|3\/4|FURG[AÃ]O|VAN|BONGO)\b/i);
  if (matchDesc) return matchDesc[0].toUpperCase().trim();

  return '-';
}

const formatCurrency = (val?: number | string) => {
  const num = Number(val || 0);
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
};

const formatDate = (dateStr?: string) => {
  return formatDateBR(dateStr);
};

const formatTime = (timeStr?: string) => {
  if (!timeStr || timeStr === '--:--' || timeStr === '') return '--:--';
  const parts = String(timeStr).split(':');
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return timeStr;
};

/**
 * Exporta a Medição e suas Ordens de Serviço Compostas para Excel (HTML-XLS formatado e compatível).
 */
export function exportMedicaoToExcel(
  medicao: MedicaoExportData,
  osList: OsItemExportData[]
): void {
  const isAgregado = medicao.type === 'agregado';
  const titulo = isAgregado
    ? `Medição de Agregado / Terceirizado #${medicao.id || 'Nova'}`
    : `Medição de Cliente #${medicao.id || 'Nova'}`;

  const entidadeLabel = isAgregado ? 'Agregado / Terceirizado' : 'Cliente';
  const entidadeValor = isAgregado
    ? medicao.agregadoNome || 'Não informado'
    : medicao.clienteNome || 'Não informado';

  const periodo = `${formatDate(medicao.periodoInicial)} até ${formatDate(medicao.periodoFinal)}`;
  const status = medicao.statusNome || 'Em Análise';
  const totalVal = Number(medicao.valorTotal || 0);

  // Nome seguro da aba: sem caracteres inválidos e max 31 chars para não corromper Excel
  const safeWorksheetName = `Medicao_${String(medicao.id || 'OS').replace(/[#:\\\/\?\*\[\]]/g, '')}`.substring(0, 31);

  // Gera planilha em formato HTML com estilos e suporte nativo a acentuação e Excel
  let html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="utf-8" />
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>${escapeXml(safeWorksheetName)}</x:Name>
              <x:WorksheetOptions>
                <x:DisplayGridlines/>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      <style>
        body { font-family: Arial, sans-serif; font-size: 11pt; }
        .header-title { font-size: 16pt; font-weight: bold; color: #b91c1c; text-align: left; }
        .sub-header { font-size: 10pt; color: #475569; }
        .meta-label { font-weight: bold; background-color: #f1f5f9; color: #1e293b; padding: 6px; }
        .meta-val { background-color: #ffffff; color: #0f172a; padding: 6px; }
        .table-head { background-color: #0f172a; color: #ffffff; font-weight: bold; text-align: center; }
        .table-row { border-bottom: 1px solid #cbd5e1; }
        .table-row:nth-child(even) { background-color: #f8fafc; }
        .cell-text { text-align: left; padding: 6px; }
        .cell-center { text-align: center; padding: 6px; }
        .cell-num { text-align: right; padding: 6px; mso-number-format:"\\R\\$\\ #\\,##0\\.00"; }
        .cell-badge { text-align: center; font-weight: bold; color: #1e293b; background-color: #f1f5f9; }
        .total-row { background-color: #fef2f2; font-weight: bold; border-top: 2px solid #b91c1c; font-size: 12pt; }
      </style>
    </head>
    <body>
      <table>
        <tr>
          <td colspan="16" class="header-title">VOOPE TRANSPORTES - RELATÓRIO DE MEDIÇÃO</td>
        </tr>
        <tr>
          <td colspan="16" class="sub-header">${escapeXml(titulo)} | Gerado em: ${escapeXml(new Date().toLocaleString('pt-BR'))}</td>
        </tr>
        <tr><td></td></tr>
        
        <tr>
          <td class="meta-label">Nº da Medição:</td>
          <td class="meta-val">#${cleanText(medicao.id || 'Nova')}</td>
          <td class="meta-label">${escapeXml(entidadeLabel)}:</td>
          <td colspan="4" class="meta-val"><b>${cleanText(entidadeValor)}</b></td>
          <td class="meta-label">Status:</td>
          <td colspan="3" class="meta-val">${cleanText(status)}</td>
          <td class="meta-label">Período:</td>
          <td colspan="4" class="meta-val">${cleanText(periodo)}</td>
        </tr>
        <tr>
          <td class="meta-label">Qtd. O.S.:</td>
          <td class="meta-val">${osList.length} ordens</td>
          <td class="meta-label">Valor Total Medição:</td>
          <td colspan="13" class="meta-val" style="color: #047857; font-weight: bold; font-size: 13pt;">${formatCurrency(totalVal)}</td>
        </tr>
        <tr><td></td></tr>

        <!-- Tabela de O.S. com Horários, Ajudantes, Tipo de Veículo, Descrição e Valor Unitário Veículo -->
        <thead>
          <tr class="table-head">
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Nº O.S.</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Data Atendimento</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">${escapeXml(isAgregado ? 'Agregado / Condutor' : 'Cliente')}</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Tipo de Veículo</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Placa</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Motorista</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Descrição</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Retirada</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Entrega</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Valor Unit. Veículo (R$)</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">HORA INÍCIO</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">HORA FIM</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">TOTAL DE HORA</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">QTD AJUDANTE</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">VALOR AJUDANTE (R$)</th>
            <th style="background-color: #1e293b; color: #ffffff; padding: 8px;">Valor Total ${escapeXml(isAgregado ? 'Repasse' : 'Serviço')} (R$)</th>
          </tr>
        </thead>
        <tbody>
  `;

  let sumValores = 0;
  let sumQtdAjudantes = 0;
  let sumValorAjudantes = 0;
  let sumValorUnitVeiculo = 0;

  osList.forEach((os) => {
    const val = isAgregado ? Number(os.valor_total_repasses || 0) : Number(os.total_os || 0);
    sumValores += val;

    const qtdAjud = Number(os.quantidade_ajudante ?? os.qtd_ajudante ?? 0);
    sumQtdAjudantes += qtdAjud;

    // Resolve valor do ajudante
    const valAjud = Number(
      os.valor_ajudante ??
      (isAgregado ? os.ajudante_repasse : (os.subtotal_viagem ?? (Number(os.valor_unit_ajud || 0) * qtdAjud))) ??
      0
    );
    sumValorAjudantes += valAjud;

    // Resolve tipo de veículo (HR, TRUCK, TOCO, etc.)
    const tipoVeiculo = resolveTipoVeiculoDisplay(os);

    // Resolve valor unitário do veículo
    const valorUnitVeiculo = Number(
      os.valor_unit_servi ??
      (qtdAjud > 0 ? (val - valAjud) : val) ??
      0
    );
    sumValorUnitVeiculo += valorUnitVeiculo;

    const nomeEntidade = isAgregado
      ? os.agregado_nome || os.motorista_nome || '-'
      : os.cliente_nome || '-';

    const hInicio = formatTime(os.horario_inicio || os.hora_inicio);
    const hFim = formatTime(os.horario_final || os.hora_fim || os.hora_termino);
    const hTotal = formatTime(os.hora_total || os.total_hora);

    html += `
      <tr class="table-row">
        <td class="cell-center">${cleanText(os.id)}</td>
        <td class="cell-center">${formatDate(os.data_atendimento)}</td>
        <td class="cell-text">${cleanText(nomeEntidade)}</td>
        <td class="cell-center" style="font-weight: bold; color: #1e293b;">${cleanText(tipoVeiculo)}</td>
        <td class="cell-center">${cleanText(os.placa || '-')}</td>
        <td class="cell-text">${cleanText(os.motorista_nome || '-')}</td>
        <td class="cell-text">${cleanText(os.descricao || '-')}</td>
        <td class="cell-text">${cleanText(os.retirada || '-')}</td>
        <td class="cell-text">${cleanText(os.entrega || '-')}</td>
        <td class="cell-num">${formatCurrency(valorUnitVeiculo)}</td>
        <td class="cell-center">${cleanText(hInicio)}</td>
        <td class="cell-center">${cleanText(hFim)}</td>
        <td class="cell-center">${cleanText(hTotal)}</td>
        <td class="cell-center">${qtdAjud || 0}</td>
        <td class="cell-num">${formatCurrency(valAjud)}</td>
        <td class="cell-num">${formatCurrency(val)}</td>
      </tr>
    `;
  });

  html += `
        <tr class="total-row">
          <td colspan="9" style="text-align: right; padding: 8px; color: #991b1b;">TOTAL GERAL DA MEDIÇÃO (${osList.length} O.S.):</td>
          <td class="cell-num" style="color: #991b1b; padding: 8px; font-weight: bold;">${formatCurrency(sumValorUnitVeiculo)}</td>
          <td colspan="3" class="cell-center" style="color: #64748b;">-</td>
          <td class="cell-center" style="color: #991b1b; padding: 8px; font-weight: bold;">${sumQtdAjudantes}</td>
          <td class="cell-num" style="color: #991b1b; padding: 8px; font-weight: bold;">${formatCurrency(sumValorAjudantes)}</td>
          <td class="cell-num" style="color: #991b1b; padding: 8px; font-size: 13pt;">${formatCurrency(sumValores || totalVal)}</td>
        </tr>
      </tbody>
    </table>
    </body>
    </html>
  `;

  const blob = new Blob(['\uFEFF' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const fileName = `Medicao_${isAgregado ? 'Agregado' : 'Cliente'}_${medicao.id ? '#' + medicao.id : 'Composta'}_${new Date().toISOString().split('T')[0]}.xls`;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Exporta a Medição e suas Ordens de Serviço Compostas para PDF formatado de alta resolução.
 */
export async function exportMedicaoToPdf(
  medicao: MedicaoExportData,
  osList: OsItemExportData[]
): Promise<void> {
  const isAgregado = medicao.type === 'agregado';
  const titulo = isAgregado
    ? `RELATÓRIO DE MEDIÇÃO - AGREGADO / TERCEIRIZADO`
    : `RELATÓRIO DE MEDIÇÃO - CLIENTE`;

  const entidadeLabel = isAgregado ? 'Agregado / Terceirizado' : 'Cliente';
  const entidadeValor = isAgregado
    ? medicao.agregadoNome || 'Não informado'
    : medicao.clienteNome || 'Não informado';

  const periodo = `${formatDate(medicao.periodoInicial)} a ${formatDate(medicao.periodoFinal)}`;
  const status = medicao.statusNome || 'Em Análise';
  const totalVal = Number(medicao.valorTotal || 0);

  // Cria elemento invisível para renderização com HTML/CSS puro
  const container = document.createElement('div');
  container.id = 'medicao-pdf-export-container';
  container.style.position = 'fixed';
  container.style.top = '-9999px';
  container.style.left = '-9999px';
  container.style.width = '900px';
  container.style.backgroundColor = '#ffffff';
  container.style.padding = '35px 40px';
  container.style.fontFamily = 'Arial, sans-serif';
  container.style.color = '#0f172a';
  container.style.boxSizing = 'border-box';

  let rowsHtml = '';
  let sumTotal = 0;

  osList.forEach((os, index) => {
    const val = isAgregado ? Number(os.valor_total_repasses || 0) : Number(os.total_os || 0);
    sumTotal += val;

    const nomeEntidade = isAgregado
      ? os.agregado_nome || os.motorista_nome || '-'
      : os.cliente_nome || '-';

    const bgClass = index % 2 === 0 ? '#ffffff' : '#f8fafc';

    rowsHtml += `
      <tr style="background-color: ${bgClass}; border-bottom: 1px solid #e2e8f0; font-size: 11px;">
        <td style="padding: 7px 8px; font-weight: bold; color: #1e293b; text-align: center;">${os.id}</td>
        <td style="padding: 7px 8px; text-align: center; color: #475569;">${formatDate(os.data_atendimento)}</td>
        <td style="padding: 7px 8px; font-weight: 500; color: #0f172a;">${nomeEntidade}</td>
        <td style="padding: 7px 8px; text-align: center; font-weight: bold; color: #1e293b;">${os.placa || '-'}</td>
        <td style="padding: 7px 8px; color: #334155;">${os.motorista_nome || '-'}</td>
        <td style="padding: 7px 8px; text-align: right; font-weight: bold; color: #0f172a; font-size: 12px;">${formatCurrency(val)}</td>
      </tr>
    `;
  });

  const finalTotal = sumTotal > 0 ? sumTotal : totalVal;

  container.innerHTML = `
    <div style="width: 100%; box-sizing: border-box;">
      
      <!-- CABEÇALHO -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #dc2626; padding-bottom: 14px; margin-bottom: 18px;">
        <div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="font-size: 26px; font-weight: 900; color: #dc2626; letter-spacing: -0.5px;">VOOPE</div>
            <div style="font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; border-left: 2px solid #cbd5e1; padding-left: 8px; line-height: 1.2;">
              Transportes &<br/>Logística
            </div>
          </div>
          <div style="font-size: 10px; color: #64748b; margin-top: 4px;">
            Transporte de Cargas & Gestão Operacional Especializada
          </div>
        </div>
        <div style="text-align: right;">
          <div style="font-size: 16px; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: 0.2px;">
            ${titulo}
          </div>
          <div style="font-size: 13px; font-weight: bold; color: #dc2626; margin-top: 2px;">
            Medição Nº #${medicao.id || 'NOVA'}
          </div>
          <div style="font-size: 10px; color: #64748b; margin-top: 2px;">
            Emissão: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>

      <!-- CARDS DE METADADOS -->
      <div style="display: grid; grid-template-columns: 2fr 1.2fr 1fr; gap: 12px; margin-bottom: 20px;">
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px;">
          <div style="font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">${entidadeLabel}</div>
          <div style="font-size: 14px; font-weight: 800; color: #0f172a; margin-top: 3px;">${entidadeValor}</div>
        </div>
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px;">
          <div style="font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">Período da Medição</div>
          <div style="font-size: 13px; font-weight: 700; color: #1e293b; margin-top: 3px;">${periodo}</div>
        </div>
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px;">
          <div style="font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;">Status</div>
          <div style="font-size: 13px; font-weight: 800; color: #047857; margin-top: 3px;">${status}</div>
        </div>
      </div>

      <!-- CARDS RESUMO VALORES -->
      <div style="display: flex; justify-content: space-between; align-items: center; background-color: #0f172a; color: #ffffff; border-radius: 8px; padding: 12px 18px; margin-bottom: 20px;">
        <div>
          <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 600;">Volume de Ordens de Serviço:</span>
          <span style="font-size: 15px; font-weight: 800; margin-left: 6px; color: #ffffff;">${osList.length} O.S.</span>
        </div>
        <div style="text-align: right;">
          <span style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; font-weight: 600;">Valor Total da Medição:</span>
          <span style="font-size: 20px; font-weight: 900; margin-left: 8px; color: #4ade80;">${formatCurrency(finalTotal)}</span>
        </div>
      </div>

      <!-- TABELA DE ORDENS DE SERVIÇO -->
      <div style="margin-bottom: 24px;">
        <div style="font-size: 12px; font-weight: 800; color: #334155; text-transform: uppercase; margin-bottom: 8px; letter-spacing: 0.3px;">
          Demonstrativo de Ordens de Serviço Compostas (${osList.length})
        </div>
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #cbd5e1; border-radius: 6px; overflow: hidden;">
          <thead>
            <tr style="background-color: #1e293b; color: #ffffff; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;">
              <th style="padding: 8px; text-align: center; width: 60px;">O.S</th>
              <th style="padding: 8px; text-align: center; width: 85px;">Data</th>
              <th style="padding: 8px; text-align: left;">${entidadeLabel}</th>
              <th style="padding: 8px; text-align: center; width: 85px;">Placa</th>
              <th style="padding: 8px; text-align: left;">Motorista</th>
              <th style="padding: 8px; text-align: right; width: 110px;">Valor (R$)</th>
            </tr>
          </thead>
          <tbody>
            ${osList.length === 0 ? `
              <tr>
                <td colspan="6" style="padding: 16px; text-align: center; color: #64748b; font-size: 12px;">
                  Nenhuma Ordem de Serviço vinculada nesta medição.
                </td>
              </tr>
            ` : rowsHtml}
          </tbody>
          <tfoot>
            <tr style="background-color: #f1f5f9; border-top: 2px solid #0f172a; font-weight: bold;">
              <td colspan="5" style="padding: 10px 12px; text-align: right; font-size: 12px; color: #0f172a; text-transform: uppercase;">
                Total da Medição (${osList.length} Ordens de Serviço):
              </td>
              <td style="padding: 10px 12px; text-align: right; font-size: 14px; color: #047857; font-weight: 900;">
                ${formatCurrency(finalTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ÁREA DE APROVAÇÃO E ASSINATURAS -->
      <div style="margin-top: 40px; padding-top: 20px; border-top: 1px dashed #cbd5e1;">
        <div style="display: flex; justify-content: space-between; gap: 40px;">
          <div style="flex: 1; text-align: center;">
            <div style="height: 45px; border-bottom: 1.5px solid #64748b;"></div>
            <div style="font-size: 11px; font-weight: 800; color: #0f172a; margin-top: 6px;">VOOPE TRANSPORTES</div>
            <div style="font-size: 9px; color: #64748b;">Departamento Operacional / Faturamento</div>
          </div>
          <div style="flex: 1; text-align: center;">
            <div style="height: 45px; border-bottom: 1.5px solid #64748b;"></div>
            <div style="font-size: 11px; font-weight: 800; color: #0f172a; margin-top: 6px;">${entidadeValor.toUpperCase()}</div>
            <div style="font-size: 9px; color: #64748b;">De Acordo / Aprovação da Medição</div>
          </div>
        </div>
      </div>

      <!-- RODAPÉ -->
      <div style="margin-top: 30px; text-align: center; font-size: 9px; color: #94a3b8; border-top: 1px solid #f1f5f9; padding-top: 10px;">
        Voope Transportes &bull; Documento gerado eletronicamente &bull; Sistema de Gestão de Transportes
      </div>

    </div>
  `;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: true,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pdfWidth = 210;
    const pdfHeight = 297;
    const imgProps = pdf.getImageProperties(imgData);
    const renderHeight = (imgProps.height * pdfWidth) / imgProps.width;

    if (renderHeight <= pdfHeight) {
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, renderHeight, undefined, 'FAST');
    } else {
      let heightLeft = renderHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, renderHeight, undefined, 'FAST');
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - renderHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, renderHeight, undefined, 'FAST');
        heightLeft -= pdfHeight;
      }
    }

    const fileName = `Medicao_${isAgregado ? 'Agregado' : 'Cliente'}_#${medicao.id || 'Nova'}.pdf`;
    pdf.save(fileName);
  } catch (err) {
    console.error('Erro ao gerar PDF da Medição:', err);
    alert('Não foi possível gerar o PDF da medição. Tente novamente.');
  } finally {
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  }
}

/**
 * Exporta listagem geral de Medições para Excel.
 */
export function exportMedicoesListToExcel(
  medicoes: any[],
  type: 'cliente' | 'agregado'
): void {
  const isAgregado = type === 'agregado';
  const titulo = isAgregado ? 'Relatório Geral de Medições de Agregados' : 'Relatório Geral de Medições de Clientes';

  let html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="utf-8" />
      <style>
        body { font-family: Arial, sans-serif; font-size: 11pt; }
        .header-title { font-size: 15pt; font-weight: bold; color: #b91c1c; }
        .table-head { background-color: #1e293b; color: #ffffff; font-weight: bold; text-align: center; }
        .table-row:nth-child(even) { background-color: #f8fafc; }
        .cell-text { text-align: left; padding: 6px; }
        .cell-center { text-align: center; padding: 6px; }
        .cell-num { text-align: right; padding: 6px; mso-number-format:"\\R\\$\\ #\\,##0\\.00"; }
        .total-row { background-color: #fef2f2; font-weight: bold; }
      </style>
    </head>
    <body>
      <table>
        <tr><td colspan="6" class="header-title">VOOPE TRANSPORTES - ${escapeXml(titulo)}</td></tr>
        <tr><td colspan="6">Data de exportação: ${escapeXml(new Date().toLocaleString('pt-BR'))} | Total de registros: ${medicoes.length}</td></tr>
        <tr><td></td></tr>
        <thead>
          <tr class="table-head">
            <th>Nº Medição</th>
            <th>${escapeXml(isAgregado ? 'Agregado / Terceirizado' : 'Cliente')}</th>
            <th>Período Inicial</th>
            <th>Período Final</th>
            <th>Status</th>
            <th>Valor Medição (R$)</th>
          </tr>
        </thead>
        <tbody>
  `;

  let totalGeral = 0;

  medicoes.forEach((m) => {
    const val = Number(m.valor_medicao || m.valor_repassar || 0);
    totalGeral += val;

    const nome = isAgregado
      ? m.agregado_nome || m.pessoa?.nome || '-'
      : m.cliente_nome || m.pedido?.pessoa?.nome || '-';

    const pIni = formatDate(m.periodo_inicial || m.data_inicial);
    const pFim = formatDate(m.periodo_final || m.data_final);
    const status = m.status_nome || m.status_medicao?.nome || 'Em Análise';

    html += `
      <tr class="table-row">
        <td class="cell-center">#${m.id}</td>
        <td class="cell-text">${cleanText(nome)}</td>
        <td class="cell-center">${cleanText(pIni)}</td>
        <td class="cell-center">${cleanText(pFim)}</td>
        <td class="cell-center">${cleanText(status)}</td>
        <td class="cell-num">${formatCurrency(val)}</td>
      </tr>
    `;
  });

  html += `
        <tr class="total-row">
          <td colspan="5" style="text-align: right; padding: 8px; font-weight: bold;">VALOR TOTAL GERAL:</td>
          <td class="cell-num" style="font-weight: bold; color: #b91c1c;">${formatCurrency(totalGeral)}</td>
        </tr>
      </tbody>
    </table>
    </body>
    </html>
  `;

  const blob = new Blob(['\uFEFF' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Lista_Medicoes_${type}_${new Date().toISOString().split('T')[0]}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
